import { select, mouse, touches } from 'd3-selection';
import { scaleBand, scaleLinear } from 'd3-scale';
import { max, sum, extent } from 'd3-array';
import { line, curveCatmullRom } from 'd3-shape';
import { axisBottom } from 'd3-axis';
import { timeFormat, timeParse } from 'd3-time-format';
import 'd3-transition';

import { getRainColourInterpolator, getWindColourInterpolator } from './interpolators';
import { convertWindBearing8 } from './convert-wind-bearing';
import { RAIN_UNIT } from './constants';
import { formatWindGust } from './formatters';
import { convertRainfall } from './unit-converters';

class DailyChart {

	constructor(data, units) {

		this.data = data;
		this.units = units;
		this.naText = 'n/a';
		this.missingValue = null;
		this.dateFormat = '%Y-%m-%d';

		this.dim = {
			marginTop: 10,
			marginRight: 10,
			marginBottom: 30,
			marginLeft: 10,

			regionSpacing: 30,
			windRegionHeight: 70,
			rainRegionHeightProp: 0.35,
			tempRegionHeightProp: 0.65,

			windSize: 50
		};

		this.keyFn = d => d.date;

	}

	render(containerNode) {

		this._init(containerNode);
		this._renderSeparators();
		this._createWindIndicatorDefs();
		this._renderWindSection();
		this._renderTemperatureSection();
		this._renderRainSection();
		this._renderAxis();
		this._setupCursor();

	}

	_init(containerNode) {

		const containerDim = containerNode.getBoundingClientRect();

		this.dim.width = containerDim.width;
		this.dim.height = 420;

		// If available width is too small, certain things will need adjusting
		this.isSmall = this.dim.width < 800;
		this.dim.windCircleRadius = this.isSmall ? 11 : 13;

		// Calculate dimensions of actual plot area
		this.dim.plotAreaWidth = this.dim.width - this.dim.marginLeft - this.dim.marginRight;
		this.dim.plotAreaHeight = this.dim.height - this.dim.marginTop - this.dim.marginBottom;

		const remainingHeight = (this.dim.height - this.dim.marginTop - this.dim.marginBottom - 2 * this.dim.regionSpacing - this.dim.windRegionHeight);

		this.dim.rainRegionHeight = remainingHeight * this.dim.rainRegionHeightProp;
		this.dim.tempRegionHeight = remainingHeight * this.dim.tempRegionHeightProp;

		// The x scale. Make sure there is good spacing between points.
		this.xScale = scaleBand()
			.domain(this.data.map(this.keyFn))
			.rangeRound([0, this.dim.plotAreaWidth])
			.paddingInner(0.25)
			.paddingOuter(0)
			.paddingOuter(0);

		const container = select(containerNode);
		container.html('');

		// Insert the SVG element and apply the margins
		this.svg = container.append('svg')
			.attr('width', this.dim.width)
			.attr('height', this.dim.height);

		// Get the SVG context we will be drawing in (simply applies the margins).
		this.plotArea = this.svg.append('g')
			.attr('class', 'hw_daily_chart_plot_area')
			.attr('transform', `translate(${this.dim.marginLeft},${this.dim.marginTop})`)
			.attr('pointer-events', 'none');

		// Background rect for whole plot area
		this.eventCapturingBox = this.plotArea.append('rect')
			.attr('class', 'hw_daily_chart_event_capturing_box')
			.attr('x', 0)
			.attr('y', 0)
			.attr('width', this.dim.plotAreaWidth)
			.attr('height', this.dim.plotAreaHeight);

	}

	_setupCursor() {

		// A cursor is a vertical line to help users line up observations in the different vertical
		// sections of the chart. It moves with the mouse pointer, or on touch screens if moves with
		// the touch location on the screen.

		const cursor = this.plotArea.append('line')
			.attr('class', 'cursor')
			.attr('x1', 0)
			.attr('x2', 0)
			.attr('y1', this.dim.marginTop)
			.attr('y2', this.dim.plotAreaHeight);

		const hideCursor = () => cursor.style('stroke-opacity', 0);
		const showCursor = () => cursor.style('stroke-opacity', 0.8);
		const positionCursor = x => cursor.attr('x1', x).attr('x2', x);

		this.eventCapturingBox.on('mouseover', showCursor);
		this.eventCapturingBox.on('mouseout', hideCursor);

		this.eventCapturingBox.on('mousemove', () => {
			const position = mouse(this.plotArea.node());
			positionCursor(position[0]);
		});

		const setCursorPositionTouch = () => {
			const position = touches(this.plotArea.node())[0];
			positionCursor(position[0]);
		};

		this.eventCapturingBox.on('touchstart', () => {
			showCursor();
			setCursorPositionTouch();
		});

		this.eventCapturingBox.on('blur', hideCursor);
		this.eventCapturingBox.on('touchmove', setCursorPositionTouch);

	}

	_createWindIndicatorDefs() {

		// Create definitions for wind icons & arrows
		const defs = this.svg.append('defs');

		const windSymbol = defs.append('g')
			.attr('id', 'wind-icon');

		// Use circle shape
		windSymbol.append('circle')
			.attr('cx', 0.5 * this.dim.windSize)
			.attr('cy', 0.5 * this.dim.windSize)
			.attr('r', this.dim.windCircleRadius);

		const y0 = 0.5 * this.dim.windSize - this.dim.windCircleRadius + 4;
		const length = 8;
		const y1 = y0 - length;
		const x0 = 0.5 * (this.dim.windSize - length * 1.2);
		const x1 = 0.5 * (this.dim.windSize + length * 1.2);

		defs.append('g')
			.attr('id', 'wind-indicator')
			.append('path')
			.attr('d', `M${x0},${y1} L${x1},${y1} L${0.5 * this.dim.windSize},${y0} L${x0},${y1}`);

	}

	_renderWindSection() {

		// Draw the arrows with <use> elements. They need to be rotated according to the
		// wind direction at a particular point.

		const windInterpolator = getWindColourInterpolator(this.units.speed);
		const windRegion = this.plotArea.append('g').attr('class', 'wind_section');
		const y = 35;

		const validGustData = this.data.filter(d => !d.empty && d.highWindGust !== this.missingValue);
		const validBearingData = this.data.filter(d => !d.empty && d.highWindGustBearing !== this.missingValue);
		const nonZeroGustBearingData = validBearingData.filter(d => !d.empty && d.highWindGust !== null && d.highWindGust > 0);

		windRegion.append('g')
			.attr('class', 'wind_icons')
			.selectAll('use')
			.data(validGustData, this.keyFn)
			.enter()
			.append('use')
			.attr('xlink:href', '#wind-icon')
			.attr('transform', d => {
				const transX = (this.xScale(d.date) + 0.5 * this.xScale.bandwidth());
				return `translate(${transX - 0.5 * this.dim.windSize},${y})`;
			})
			.style('fill', d => windInterpolator(d.highWindGust));

		// Draw wind indicators
		if (this.dim.width > 900) {
			windRegion.append('g')
				.attr('class', 'wind_arrows')
				.selectAll('use')
				.data(nonZeroGustBearingData, this.keyFn)
				.enter()
				.append('use')
				.attr('xlink:href', '#wind-indicator')
				.attr('transform', d => {
					const transX = (this.xScale(d.date) + 0.5 * this.xScale.bandwidth());
					const rotate = 45 * Math.round(d.highWindGustBearing / 45);
					return `translate(${transX - 0.5 * this.dim.windSize},35) rotate(${rotate} ${0.5 * this.dim.windSize} ${0.5 * this.dim.windSize})`;
				});
		}

		// Draw the labels for wind direction and speed
		windRegion.append('g')
			.attr('class', 'wind_gust_labels')
			.selectAll('text.wind_gust_label')
			.data(this.data, this.keyFn)
			.enter()
			.append('text')
			.attr('class', d => d.empty || (d.highWindGust === this.missingValue) ? 'missing_label' : null)
			.attr('x', d => this.xScale(d.date) + 0.5 * this.xScale.bandwidth())
			.attr('y', y + 0.5 * this.dim.windSize)
			.attr('dy', '0.35em')
			.attr('text-anchor', 'middle')
			.text(d => d.empty || (d.highWindGust === this.missingValue)
				? this.naText
				: formatWindGust(d.highWindGust, this.units.speed, true, true)
			);

		windRegion.append('g')
			.attr('class', 'wind_direction_labels')
			.selectAll('text.wind_direction_label')
			.data(nonZeroGustBearingData, this.keyFn)
			.enter()
			.append('text')
			.attr('x', d => this.xScale(d.date) + 0.5 * this.xScale.bandwidth())
			.attr('y', y)
			.attr('text-anchor', 'middle')
			.text(d => convertWindBearing8(d.highWindGustBearing));

		// Draw wind label in top left of wind region
		windRegion.append('text')
			.attr('class', 'section_label')
			.attr('x', 5)
			.attr('y', 5)
			.attr('dy', '0.8em')
			.text(`Peak wind gust (${this.units.speed})`);

	}

	_renderTemperatureSection() {

		// The temperature scale
		const tempScale = scaleLinear()
			.range([
				this.dim.plotAreaHeight - this.dim.rainRegionHeight - this.dim.regionSpacing,
				this.dim.windRegionHeight + this.dim.regionSpacing
			]);

		const minTemps = this.data.map(d => d.lowTemperature)
			.filter(val => val !== this.missingValue);

		const maxTemps = this.data.map(d => d.highTemperature)
			.filter(val => val !== this.missingValue);

		let tempScaleLimits = extent([].concat(minTemps).concat(maxTemps));

		if (minTemps.length === 0 && maxTemps.length === 0) {
			tempScaleLimits = [0, 20];
		}

		const tempScaleRange = tempScaleLimits[1] - tempScaleLimits[0];

		// Pad out the temperature axis limits, more so at the top.
		// If there is no variation throughout the period, use +/- 1 degree
		tempScaleLimits[0] = Math.floor(tempScaleLimits[0]) - Math.max(1, 0.15 * tempScaleRange);
		tempScaleLimits[1] = Math.ceil(tempScaleLimits[1]) + Math.max(1, 0.25 * tempScaleRange);

		// Set the domain of the temperature scale
		tempScale.domain(tempScaleLimits);

		const heightTween = d => {
			return t => t * d;
		};

		const tempRegion = this.plotArea.append('g')
			.attr('class', 'temperature_section');

		tempRegion.append('g')
			.attr('class', 'temperature_bars_high')
			.selectAll('rect.temperature_bar_high')
			.data(this.data.filter(d => !d.empty && d.avgTemperature !== this.missingValue && d.highTemperature !== this.missingValue), this.keyFn)
			.enter()
			.append('rect')
			.attr('transform', d => `translate(${this.xScale(d.date)},${tempScale(d.avgTemperature)}) scale(1,-1)`)
			.attr('width', this.xScale.bandwidth())
			.attr('height', 0)
			.transition()
			.duration(500)
			.attrTween('height', d => heightTween(tempScale(d.avgTemperature) - tempScale(Math.round(d.highTemperature))));

		tempRegion.append('g')
			.attr('class', 'temperature_bars_low')
			.selectAll('rect.temperature_bar_low')
			.data(this.data.filter(d => !d.empty && d.avgTemperature !== this.missingValue && d.lowTemperature !== this.missingValue), this.keyFn)
			.enter()
			.append('rect')
			.attr('x', d => this.xScale(d.date))
			.attr('y', d => tempScale(d.avgTemperature))
			.attr('width', this.xScale.bandwidth())
			.attr('height', 0)
			.transition()
			.duration(500)
			.attrTween('height', d => heightTween(tempScale(Math.round(d.lowTemperature)) - tempScale(d.avgTemperature)));

		// D3 line function for calculating temperature path coordinates
		const avgTemperatureLine = line()
			.x(d => this.xScale(d.date) + 0.5 * this.xScale.bandwidth())
			.y(d => tempScale(d.avgTemperature))
			.defined(d => !d.empty && d.avgTemperature !== this.missingValue)
			.curve(curveCatmullRom);

		// Draw the temperature lines
		tempRegion.append('path')
			.attr('class', 'temperature_avg_path')
			.attr('d', avgTemperatureLine(this.data));

		// Draw the high temperature labels
		tempRegion.append('g')
			.attr('class', 'temperature_high_labels')
			.selectAll('text.temperature_high_label')
			.data(this.data.filter(d => !d.empty && d.highTemperature !== this.missingValue), this.keyFn)
			.enter()
			.append('text')
			.attr('x', d => this.xScale(d.date) + 0.5 * this.xScale.bandwidth())
			.attr('y', d => d.highTemperature === this.missingValue ? 0 : tempScale(Math.round(d.highTemperature)))
			.attr('dy', '-0.5em')
			.attr('text-anchor', 'middle')
			.text(d => d.highTemperature === this.missingValue ? '' : d.highTemperature);

		// Draw the low temperature labels
		tempRegion.append('g')
			.attr('class', 'temperature_low_labels')
			.selectAll('text.temperature_low_label')
			.data(this.data.filter(d => !d.empty && d.lowTemperature !== this.missingValue), this.keyFn)
			.enter()
			.append('text')
			.attr('x', d => this.xScale(d.date) + 0.5 * this.xScale.bandwidth())
			.attr('y', d => d.lowTemperature === this.missingValue ? 0 : tempScale(Math.round(d.lowTemperature)))
			.attr('dy', '1.2em')
			.attr('text-anchor', 'middle')
			.text(d => d.lowTemperature === this.missingValue ? '' : d.lowTemperature);

		// Draw the temperature region and wind region separator
		tempRegion.append('line')
			.attr('class', 'region_separator')
			.attr('transform', `translate(0,${this.dim.windRegionHeight + 0.5 * this.dim.regionSpacing})`)
			.attr('x1', 0)
			.attr('x2', this.dim.plotAreaWidth)
			.attr('y1', 0)
			.attr('y2', 0);

		// Draw temperature label in top left of temperature region
		tempRegion.append('text')
			.attr('class', 'section_label')
			.attr('x', 5)
			.attr('y', this.dim.windRegionHeight + this.dim.regionSpacing / 2)
			.attr('dy', '1.3em')
			.text(`High and low temperatures (°${this.units.temperature})`);

	}

	_renderRainSection() {

		const lowestRainScaleMax = this.units.rain === RAIN_UNIT.IN ? 0.4 : 10;
		const rainDecimals = this.units.rain === RAIN_UNIT.IN ? 2 : 1;

		// Filter any NA values
		const rainTotals = this.data
			.map(d => d.rainfall)
			.filter(r => r !== this.missingValue);

		// The rain scale. Leave space at top for labels.
		const rainScale = scaleLinear()
			.range([0, this.dim.rainRegionHeight - 30])
			.domain([0, Math.max(lowestRainScaleMax, max(rainTotals))])
			.clamp(true);

		const rainInterpolator = getRainColourInterpolator(convertRainfall(50, this.units.rain));
		const rainRegion = this.plotArea.append('g').attr('class', 'rain_section');

		// Insert the rainfall bars
		rainRegion.append('g')
			.attr('class', 'rain_bars')
			.selectAll('rect.rain_bar')
			.data(this.data.filter(d => !d.empty && d.rainfall !== this.missingValue && d.rainfall > 0), this.keyFn)
			.enter()
			.append('rect')
			.style('fill', d => rainInterpolator(d.rainfall))
			.attr('x', d => this.xScale(d.date))
			.attr('y', d => this.dim.plotAreaHeight - rainScale(d.rainfall))
			.attr('width', this.xScale.bandwidth())
			.attr('height', d => rainScale(d.rainfall));

		// Insert the bar labels. For zero rainfall, insert a small dot instead, and increase its font size.
		// Remember to check values are all at least 0.
		rainRegion.append('g')
			.attr('class', 'rain_labels')
			.selectAll('text.rain_label')
			.data(this.data, this.keyFn)
			.enter()
			.append('text')
			.attr('class', d => {
				if (d.rainfall === 0) {
					return 'rain_zero';
				}
				if (d.empty || d.rainfall === this.missingValue) {
					return 'missing_label';
				}
				return 'rain_label';
			})
			.attr('text-anchor', 'middle')
			.attr('x', d => this.xScale(d.date) + 0.5 * this.xScale.bandwidth())
			.attr('y', d => {
				if (d.empty || d.rainfall === this.missingValue) {
					return this.dim.plotAreaHeight - 8;
				}
				const r = (d.rainfall === this.missingValue ? 0 : d.rainfall);
				return this.dim.plotAreaHeight - rainScale(r) - 8;
			})
			.text(d => {
				if (d.empty || d.rainfall === this.missingValue) {
					return this.naText;
				}
				if (d.rainfall > 0) {
					const precision = Math.pow(10, rainDecimals);
					let label = Math.round(precision * d.rainfall) / precision;
					// For 2 dp values, remove leading zero
					//  so labels are not too crammed together
					if (label < 0.1) {
						label = `${label}`.substring(1);
					}
					return label;
				}
				return '.';
			});

		// Draw rainfall label in top left of rain region
		rainRegion.append('text')
			.attr('class', 'section_label')
			.attr('x', 5)
			.attr('y', this.dim.plotAreaHeight - this.dim.rainRegionHeight - 0.5 * this.dim.regionSpacing)
			.attr('dy', '1.3em')
			.text(`Daily rainfall (${this.units.rain})`);

		// Draw rain total label at top-right of rain region
		rainRegion.append('text')
			.attr('class', 'statistic_label')
			.attr('x', this.dim.plotAreaWidth - 5)
			.attr('y', this.dim.plotAreaHeight - this.dim.rainRegionHeight - 0.5 * this.dim.regionSpacing)
			.attr('text-anchor', 'end')
			.attr('dy', '1.3em')
			.text('Total: ' + sum(rainTotals).toFixed(rainDecimals));

		// Add line to separate rain region from temperature region
		rainRegion.append('line')
			.attr('class', 'region_separator')
			.attr('transform', 'translate(0,' + (this.dim.plotAreaHeight - this.dim.rainRegionHeight - 0.5 * this.dim.regionSpacing) + ')')
			.attr('x1', 0)
			.attr('x2', this.dim.plotAreaWidth)
			.attr('y1', 0)
			.attr('y2', 0);

	}

	_renderSeparators() {

		// Insert day separator(s) between months. To do so, need to get the positions
		// of the two points and average them, then add half the interval width.

		const dateFormat = timeFormat(this.dateFormat);
		const dateParser = timeParse(dateFormat);

		const daysOfMonth = this.data.map(d => dateParser(d.date).getDate());
		const numPoints = this.data.length;

		let indexOfNewMonth = daysOfMonth.indexOf(1);

		while (indexOfNewMonth !== -1) {

			if (indexOfNewMonth < numPoints && indexOfNewMonth > 0) {

				const prevMonthDate = this.data[indexOfNewMonth - 1].date;
				const currMonthDate = this.data[indexOfNewMonth].date;
				const newMonthLineX = 0.5 * (this.xScale(prevMonthDate) + this.xScale(currMonthDate) + this.xScale.bandwidth());

				this.plotArea.append('line')
					.attr('class', 'month_separator')
					.attr('x1', newMonthLineX)
					.attr('x2', newMonthLineX)
					.attr('y1', this.dim.marginTop)
					.attr('y2', this.dim.plotAreaHeight + 20);
			}

			indexOfNewMonth = daysOfMonth.indexOf(1, indexOfNewMonth + 1);

		}

	}

	_renderAxis() {

		// Create the x axis, with no ticks showing.
		const xAxis = axisBottom()
			.scale(this.xScale)
			.tickSizeInner(0)
			.tickSizeOuter(0)
			.tickFormat(date => parseInt(date.substr(-2), 10));

		// Draw the x axis and labels
		this.plotArea.append('g')
			.attr('class', 'date_axis')
			.attr('transform', `translate(0,${this.dim.plotAreaHeight})`)
			.call(xAxis)
			.selectAll('text')
			.attr('dy', '1.4em');

	}

}

export default DailyChart;



// WEBPACK FOOTER //
// ./utils/daily-chart.js